// Imports
import { faCheck, faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components/macro';
import theme from '../../theme';
import planMapping from '../../utils/billing/plan-mapping';
import { Plan } from './plans-details';


// Styled components
const Wrapper = styled.div<{ containerWidth: string; backgroundColor: string; isDisabled: boolean; isFaded: boolean }>`
	display: flex;
	width: ${({ containerWidth }) => containerWidth};
	padding: 1.25rem;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.75rem;
	line-height: 1;
	
	border-radius: 0.625rem;
	background-color: ${({ backgroundColor, isDisabled }) => (isDisabled ? 'hsl(0, 0%, 87%)' : backgroundColor)};
	
	position: relative;
	cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
	
	opacity: ${({ isFaded }) => (isFaded ? '0.5' : '1')};
	
	@media (max-width: ${theme.breakpoints.desktop}px) {
		width: 15.375rem;
	}
`;

const HeadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
`;

const Heading = styled.h2<{ color: string; isDisabled: boolean }>`
	color: ${({ color, isDisabled }) => (isDisabled ? 'hsl(0, 0%, 35%)' : color)};
	font-size: 1.875rem;
	font-weight: 600;
`;

const FeaturesContainer = styled.figure`
	display: block;
`;

const FeaturesHeading = styled.figcaption`
	font-size: 0.75rem;
	font-weight: 400;
	text-transform: uppercase;
	margin-bottom: 0.625rem;
`;

const Features = styled.ul`
	display: flex;
	gap: 0.625rem;
	list-style: none;
	flex-direction: column;
	
	margin-left: 0;
	line-height: 1;
`;

const Feature = styled.li`
	display: flex;
	align-items: center;
	gap: 0.375rem;
	font-size: 0.875rem;
`;

const IconLayers = styled.span`
	position: absolute;
	top: 0;
	left: -0.129rem;
	
	font-size: 1rem;
`;

const Checkbox = styled.div`
	position: relative;
	
	width: 1rem;
	height: 1rem;
	min-width: 1rem;
`;

const Pill = styled.div<{ backgroundColor: string; isDisabled: boolean }>`
	margin-top: 0.625rem;
	border-radius: 624.9375rem;
	background-color: ${({ backgroundColor, isDisabled }) => (isDisabled ? 'hsl(0, 0%, 70%)' : backgroundColor)};
	${({ isDisabled }) =>
		!isDisabled &&
		css`
			color: hsl(0, 0%, 100%);
		`};
	font-weight: 500;
	text-align: center;
	
	display: flex;
	padding: 0.375rem 0.875rem;
	
	align-self: center;
`;

const PillContent = styled.div<{ displayCheck: boolean }>`
	display: flex;
	align-items: center;
	
	.svg-inline--fa {
		${({ displayCheck }) =>
			displayCheck
				? css`
						opacity: 1;
						width: 0.875rem;
						margin-right: 0.375rem;
					`
				: css`
						opacity: 0;
						width: 0;
						pointer-events: none;
					`}
		
		transition:
			opacity 0.3s ease-in-out,
			width 0.3s ease;
	}
`;


// Define the accepted props
interface Props {
	plan: Plan;
	isDisabled: boolean;
	isFaded: boolean;
	pillContent: string | ReactElement;
	isChecked: boolean;
}


// Function component
const PlanCard: React.FC<Props> = ({
	plan: {
		name,
		planNumber,
		backgroundColor,
		accentColor,
		features: { features, label },
	},
	isDisabled,
	isFaded,
	pillContent,
	isChecked,
}) => {
	// Return JSX
	return (
		<Wrapper
			containerWidth={
				planNumber === planMapping.engage || planNumber === planMapping.grandfatheredDeluxe ? '15.375rem' : '14.625rem'
			}
			backgroundColor={backgroundColor}
			isDisabled={isDisabled}
			isFaded={isFaded}
		>
			<HeadingContainer>
				<Heading color={accentColor} isDisabled={isDisabled}>
					{name}
				</Heading>
			</HeadingContainer>
			
			<FeaturesContainer>
				<FeaturesHeading>{label}</FeaturesHeading>
				
				<Features>
					{features.map(({ text: featureText }) => (
						<Feature key={featureText}>
							<Checkbox>
								<IconLayers className='fa-layers fa-fw'>
									<FontAwesomeIcon icon={faCircle} color={isDisabled ? 'hsl(0, 0%, 35%)' : accentColor} />
									<FontAwesomeIcon icon={faCheck} color='hsl(0, 0%, 100%)' transform='shrink-6.36' />
								</IconLayers>
							</Checkbox>
							{featureText}
						</Feature>
					))}
				</Features>
			</FeaturesContainer>
			
			<Pill backgroundColor={accentColor} isDisabled={isDisabled || planNumber === planMapping.grandfatheredDeluxe}>
				<PillContent displayCheck={isChecked}>
					<FontAwesomeIcon
						icon={faCheck}
						color={planNumber !== planMapping.grandfatheredDeluxe ? 'hsl(0, 0%, 100%)' : undefined}
					/>
					{pillContent}
				</PillContent>
			</Pill>
		</Wrapper>
	);
};

export default PlanCard;
